// Port of Services/SBotTools/SBotToolApprovalStore.swift — in-memory,
// single-use, user-bound approval records with a 30-minute TTL. Matches iOS:
// approvals do not survive a reload; the model must re-prepare.

import type { ChatToolValue } from "axon-modal-chat-kit/core";
import type { SBotToolApprovalCard } from "./toolEnvelope";

export type SBotToolActionType =
  | "broadcastOverlaySkinMutation"
  | "leagueSettingsUpdate"
  | "leagueInvitation"
  | "athleteSchemaUpdate"
  | "competitionTemplateUpdate"
  | "organizationAdminSchemaUpdate"
  | "competitionCreation"
  | "competitionUpdate"
  | "athleteCreation"
  | "broadcastOverlayInputStateUpdate"
  | "broadcastOverlayInputParameterUpdate"
  | "broadcastOverlayInputCreation";

export type SBotToolApprovalStatus = "pending" | "approved" | "rejected" | "executed";

export interface SBotToolApprovalRecord {
  id: string;
  userID: string;
  actionType: SBotToolActionType;
  title: string;
  summary: string;
  leagueID: string;
  targetHandle?: string | undefined;
  payload: Record<string, ChatToolValue>;
  status: SBotToolApprovalStatus;
  createdAt: Date;
  expiresAt: Date;
  executedAt?: Date | undefined;
}

export class SBotToolApprovalError extends Error {
  readonly kind: "notFound" | "wrongUser" | "expired" | "notPending" | "notApproved" | "actionMismatch";

  constructor(kind: SBotToolApprovalError["kind"]) {
    super(APPROVAL_ERROR_MESSAGES[kind]);
    this.name = "SBotToolApprovalError";
    this.kind = kind;
  }
}

const APPROVAL_ERROR_MESSAGES = {
  notFound: "The approval could not be found.",
  wrongUser: "This approval belongs to a different user.",
  expired: "The approval has expired. Prepare the action again.",
  notPending: "This approval is no longer pending.",
  notApproved: "User approval is required before this action can run.",
  actionMismatch: "The approval is for a different action.",
} as const;

const DEFAULT_TTL_MS = 30 * 60 * 1000;

export function approvalRecordCard(record: SBotToolApprovalRecord): SBotToolApprovalCard {
  return {
    approvalID: record.id,
    title: record.title,
    summary: record.summary,
    status: record.status,
    actionType: record.actionType,
    leagueID: record.leagueID,
    targetHandle: record.targetHandle,
    expiresAt: record.expiresAt,
  };
}

export class SBotToolApprovalStore {
  private readonly records = new Map<string, SBotToolApprovalRecord>();
  private readonly ttlMs: number;

  constructor(ttlMs: number = DEFAULT_TTL_MS) {
    this.ttlMs = ttlMs;
  }

  create(init: {
    userID: string;
    actionType: SBotToolActionType;
    title: string;
    summary: string;
    leagueID: string;
    targetHandle?: string | undefined;
    payload: Record<string, ChatToolValue>;
  }): SBotToolApprovalRecord {
    const now = new Date();
    const record: SBotToolApprovalRecord = {
      id: `sbotApproval:${crypto.randomUUID().toLowerCase()}`,
      userID: init.userID,
      actionType: init.actionType,
      title: init.title,
      summary: init.summary,
      leagueID: init.leagueID,
      targetHandle: init.targetHandle,
      payload: init.payload,
      status: "pending",
      createdAt: now,
      expiresAt: new Date(now.getTime() + this.ttlMs),
    };
    this.records.set(record.id, record);
    return record;
  }

  record(id: string): SBotToolApprovalRecord | undefined {
    return this.records.get(id);
  }

  approve(id: string, userID: string): SBotToolApprovalRecord {
    const record = this.pendingRecord(id, userID);
    record.status = "approved";
    return record;
  }

  reject(id: string, userID: string): SBotToolApprovalRecord {
    const record = this.pendingRecord(id, userID);
    record.status = "rejected";
    return record;
  }

  /** Single-use: transitions approved → executed; a second call throws notApproved. */
  consumeApproved(id: string, userID: string, actionType?: SBotToolActionType): SBotToolApprovalRecord {
    const record = this.records.get(id);
    if (!record) throw new SBotToolApprovalError("notFound");
    if (record.userID !== userID) throw new SBotToolApprovalError("wrongUser");
    if (record.expiresAt.getTime() <= Date.now()) {
      record.status = "rejected";
      throw new SBotToolApprovalError("expired");
    }
    if (record.status !== "approved") throw new SBotToolApprovalError("notApproved");
    if (actionType !== undefined && record.actionType !== actionType) throw new SBotToolApprovalError("actionMismatch");
    record.status = "executed";
    record.executedAt = new Date();
    return record;
  }

  private pendingRecord(id: string, userID: string): SBotToolApprovalRecord {
    const record = this.records.get(id);
    if (!record) throw new SBotToolApprovalError("notFound");
    if (record.userID !== userID) throw new SBotToolApprovalError("wrongUser");
    if (record.expiresAt.getTime() <= Date.now()) throw new SBotToolApprovalError("expired");
    if (record.status !== "pending") throw new SBotToolApprovalError("notPending");
    return record;
  }
}
